import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setUser(res.data.user);
    return res.data.user;
  };

  const signup = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/signup`, { email, password, name });
    setUser(res.data.user);
    return res.data.user;
  };

  const googleSession = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/session`, null, {
      headers: { 'X-Session-ID': sessionId },
    });
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {}
    setUser(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Setup axios interceptor for 401 handling
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Normalise structured error bodies (e.g. FastAPI 429 rate-limit
        // response is `{detail: {error, scope, limit, retry_after_seconds,
        // message}}`).  Many call sites do `setError(err.response.data.detail)`
        // and later render `{error}` — passing a raw object into JSX crashes
        // React with "Objects are not valid as a React child".  Coerce
        // `detail` to a human-readable string here so every downstream
        // consumer gets a safe value.
        try {
          const data = error?.response?.data;
          if (data && typeof data.detail === 'object' && data.detail !== null) {
            const d = data.detail;
            data.detail = d.message
              || (d.error ? String(d.error) : null)
              || JSON.stringify(d);
          }
        } catch (_) { /* never let the interceptor throw */ }

        const original = error.config;
        if (error.response?.status === 401 && !original._retry && !original.url?.includes('/auth/')) {
          original._retry = true;
          try {
            await axios.post(`${API}/auth/refresh`);
            return axios(original);
          } catch (e) {
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, googleSession, logout, checkAuth, API }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
